import React, { useState } from 'react'
import { Platform, Pressable } from 'react-native'
import { useNavigate } from 'react-router-dom'

import { CSSObject } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import { getTypeStyles } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { openUrl } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/expo-linking'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'

// Callers (faqConstants, InternationalFaqConstants) put bare text inside
// AnswerBody / AnswerListItem. That works on react-native-web because text
// styles cascade through the DOM, but would need <Text> wrappers to render on
// actual React Native — true crossplatform support is future work.

// `display: block` is a RN-Web extension required so inline <a> children flow
// within the paragraph instead of stacking as flex-column rows.
export const AnswerBody = styled.View<{ theme: ThemeType }>(
  ({ theme }): CSSObject => ({
    ...(getTypeStyles(theme.colors, theme.fontFamilyMetadata).bodyDefault as CSSObject),
    color: theme.colors.textPrimary,
    display: 'block',
  }),
)

export const AnswerParagraph = styled.View<{ theme: ThemeType }>(
  ({ theme }): CSSObject => ({
    marginBottom: theme.spacing['16px'],
    display: 'block',
  }),
)

export const AnswerList = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['16px'],
  paddingLeft: theme.spacing['24px'],
}))

export const AnswerOrderedList = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['16px'],
  paddingLeft: theme.spacing['24px'],
}))

const AnswerListItemRow = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  marginBottom: theme.spacing['8px'],
}))

// Slot height = body line-height (24) so the bullet aligns with the first line
// of text regardless of font metrics.
const AnswerListItemBulletSlot = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  height: 24,
  justifyContent: 'center',
  marginRight: theme.spacing['12px'],
}))

const AnswerListItemBullet = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  width: 6,
  height: 6,
  borderRadius: 4,
  backgroundColor: theme.colors.textPrimary,
}))

// See AnswerBody — `display: block` keeps inline children flowing.
const AnswerListItemContent = styled.View(() => ({
  flex: 1,
  display: 'block',
}))

export const AnswerListItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AnswerListItemRow>
    <AnswerListItemBulletSlot>
      <AnswerListItemBullet />
    </AnswerListItemBulletSlot>
    <AnswerListItemContent>{children}</AnswerListItemContent>
  </AnswerListItemRow>
)

// eslint-disable-next-line lyrahealth/no-react-native-text
export const AnswerEmphasis = styled.Text({
  fontStyle: 'italic',
})

export const AnswerLineBreak = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  height: theme.spacing['16px'],
}))

type AnswerLinkProps = {
  to?: string
  href?: string
  children: React.ReactNode
}

// eslint-disable-next-line lyrahealth/no-react-native-text
const AnswerLinkText = styled.Text<{ theme: ThemeType; isHovered: boolean }>(({ theme, isHovered }) => ({
  color: isHovered ? theme.colors.linkHover : theme.colors.linkDefault,
  textDecorationLine: 'underline',
  fontWeight: '700',
}))

// Uses a real <a> on web (not Pressable) so the link flows inline with
// surrounding paragraph text and gets cmd-click, mailto/tel handling, and
// anchor a11y for free. For internal `to=` links we preventDefault and use
// react-router's navigate to avoid a full page reload.
export const AnswerLink: React.FC<AnswerLinkProps> = ({ to, href, children }) => {
  const [isHovered, setIsHovered] = useState(false)
  const navigate = useNavigate()
  const { colors } = useTheme() as ThemeType

  if (Platform.OS === 'web') {
    const url = to || href || ''
    const isExternal = !to && !!href
    return (
      <a
        href={url}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noreferrer' : undefined}
        onClick={(e) => {
          if (to) {
            e.preventDefault()
            navigate(to)
          }
          // Let the browser handle href clicks so cmd-click and mailto:/tel:
          // schemes work without our intervention.
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          color: isHovered ? colors.linkHover : colors.linkDefault,
          textDecoration: 'underline',
          fontWeight: 'bold',
          cursor: 'pointer',
        }}
      >
        {children}
      </a>
    )
  }

  return (
    <Pressable
      accessibilityRole='link'
      onPress={() => {
        if (to) {
          navigate(to)
          return
        }
        if (href) {
          openUrl(href)
        }
      }}
    >
      <AnswerLinkText isHovered={isHovered}>{children}</AnswerLinkText>
    </Pressable>
  )
}
